import { useMemo } from 'react'

import { create } from '@bufbuild/protobuf'
import { useQuery } from '@connectrpc/connect-query'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { AreaWeatherForecast } from '@ebalance/proto/bffuser/v1/weather_forecast_model_pb'
import { batchGetAreaWeatherForecasts } from '@ebalance/proto/bffuser/v1/weather_forecast_service-WeatherForecastService_connectquery'
import {
  BatchGetAreaWeatherForecastsRequestSchema,
  type BatchGetAreaWeatherForecastsResponse,
} from '@ebalance/proto/bffuser/v1/weather_forecast_service_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'

import {
  indexByArea,
  useNormalizedAreaCodes,
  useSameDatePlaceholder,
} from '~/hooks/weather/batchQuery'
import { WEATHER_REFETCH_INTERVAL_MS } from '~/hooks/weather/refetchInterval'

export type UseAreaWeatherForecastsParams = {
  areaCodes: readonly AreaCode[]
  /** 対象日 (単日)。`from = to = targetDate` で取る。 */
  targetDate: ProtoDate
  enabled?: boolean
}

/**
 * 複数エリアの予報を batch RPC (WeatherForecastService.BatchGetAreaWeatherForecasts)
 * 1 本で取り、area_code で引ける Map で返す。area 列が空・`enabled: false` の間は
 * RPC を出さない。再取得は `WEATHER_REFETCH_INTERVAL_MS` (120 秒)。
 *
 * fail-safe: 取得失敗は error boundary に飛ばさず (`throwOnError: false`) 空の
 * Map に降格する (doji-bg AC-372 相当)。同じ対象日で area 列だけ変わった時は
 * 新しい応答まで前の応答を出し続け、対象日が変わった時は前日の値を出さない。
 */
export function useAreaWeatherForecasts(
  params: UseAreaWeatherForecastsParams,
): Map<AreaCode, AreaWeatherForecast> {
  const areaCodes = useNormalizedAreaCodes(params.areaCodes)
  const { placeholderData, track } =
    useSameDatePlaceholder<BatchGetAreaWeatherForecastsResponse>(
      params.targetDate,
    )
  const result = useQuery(
    batchGetAreaWeatherForecasts,
    create(BatchGetAreaWeatherForecastsRequestSchema, {
      areaCodes,
      targetDateFrom: params.targetDate,
      targetDateTo: params.targetDate,
    }),
    {
      enabled: (params.enabled ?? true) && areaCodes.length > 0,
      throwOnError: false,
      refetchInterval: WEATHER_REFETCH_INTERVAL_MS,
      placeholderData,
    },
  )
  track(result)

  return useMemo(
    () => indexByArea(result.data?.forecasts, areaCodes),
    [result.data, areaCodes],
  )
}
